import { useState } from 'react';
import { useTranslation } from '@doccareplus/i18n';
import { EmptyState, LoadingSpinner, Modal } from '@doccareplus/ui';
import { useMyPrescriptionQuery, useMyPrescriptionsQuery } from '../application/records.query';
import type { PortalPrescriptionDto, PortalPrescriptionSummaryDto } from '../infrastructure/records.api';
import { PortalLoadError } from '@/shared/ui/PortalLoadError';
import { PortalPrescriptionDocument } from '@/shared/prescription/PortalPrescriptionDocument';
import { PrescriptionsListSkeleton } from './RecordsSkeletons';

// Row/detail rendering below intentionally mirrors (does not import) sharedDocuments' PrescriptionView —
// cross-feature imports are forbidden by .ai/frontend-architecture.md, and the shape is small enough
// (~40 lines) that duplicating it here reads clearer than extracting a shared primitive for one caller.

function DocumentIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-[var(--text-3)]">
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function PrescriptionRow({
  prescription,
  onView,
}: {
  prescription: PortalPrescriptionSummaryDto;
  onView: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onView}
      className="flex w-full items-center gap-3 rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 text-left shadow-[var(--shadow-1)] transition-colors duration-200 hover:bg-[var(--surface-2)]"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--primary-weak)] text-[var(--primary)]">
        <DocumentIcon />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold">{prescription.providerDisplayName}</div>
        <div className="text-[12px] text-[var(--text-3)]">
          {new Date(prescription.visitDate).toLocaleDateString()}
        </div>
        {prescription.medicinesPreview && (
          <div className="mt-0.5 truncate text-[12.5px] text-[var(--text-2)]">{prescription.medicinesPreview}</div>
        )}
      </div>
      <ChevronIcon />
    </button>
  );
}

function PrescriptionDetailModal({ visitId, onClose }: { visitId: string; onClose: () => void }) {
  const { t } = useTranslation('portal');
  const detail = useMyPrescriptionQuery(visitId);

  return (
    <Modal title={t('records.detail.title', 'Prescription')} onClose={onClose} closeLabel={t('records.detail.close', 'Close')}>
      {detail.isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : detail.isError ? (
        <PortalLoadError compact onRetry={() => void detail.refetch()} />
      ) : detail.data ? (
        <PrescriptionDetailBody prescription={detail.data} />
      ) : null}
    </Modal>
  );
}

function PrescriptionDetailBody({ prescription }: { prescription: PortalPrescriptionDto }) {
  return <PortalPrescriptionDocument prescription={prescription} layout={prescription.layout} />;
}

export function RecordsPage() {
  const { t } = useTranslation('portal');
  const prescriptions = useMyPrescriptionsQuery();
  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(null);

  if (prescriptions.isLoading) {
    return <PrescriptionsListSkeleton label="Loading prescriptions" />;
  }

  if (prescriptions.isError) {
    return <PortalLoadError onRetry={() => void prescriptions.refetch()} />;
  }

  const list = prescriptions.data?.prescriptions ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="md:hidden">
        <h1 className="text-[23px] font-bold tracking-[-0.01em]">{t('records.title', 'Records')}</h1>
        <p className="mt-1 text-[12.5px] text-[var(--text-3)]">{t('records.heldByClinic', 'Held by your selected clinic only')}</p>
      </div>

      <div>
        <section className="flex flex-col gap-3">
          <h2 className="text-[15px] font-bold">
            {t('records.prescriptionsSection', 'Prescriptions')}
          </h2>
          {list.length > 0 ? (
            list.map((prescription) => (
              <PrescriptionRow
                key={prescription.visitId}
                prescription={prescription}
                onView={() => setSelectedVisitId(prescription.visitId)}
              />
            ))
          ) : (
            <EmptyState variant="animated" title={t('records.noPrescriptions', 'No prescriptions yet.')} />
          )}
        </section>

      </div>

      {selectedVisitId && (
        <PrescriptionDetailModal visitId={selectedVisitId} onClose={() => setSelectedVisitId(null)} />
      )}
    </div>
  );
}
